import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { usePlannerDraft } from '../hooks/usePlannerDraft.js';
import type { TabId } from '../types.js';
import { PlannerConfig } from './planner/PlannerConfig.js';
import { PlanDraftReview } from './planner/PlanDraftReview.js';

type Mode = 'config' | 'review';

export interface PlannerProps {
  onNavigate?: (tab: TabId) => void;
}

export function Planner({ onNavigate }: PlannerProps = {}): ReactElement {
  const draft = usePlannerDraft();
  const [mode, setMode] = useState<Mode>('config');

  useEffect(() => {
    if (draft.data && draft.data.plan !== null && draft.data.days.length > 0) {
      setMode('review');
    }
  }, [draft.data]);

  return (
    <section
      className="flex h-full flex-col"
      data-testid="planner-view"
    >
      <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
        <button
          type="button"
          data-testid="planner-mode-config"
          onClick={() => setMode('config')}
          className="flex-1 py-2 text-sm"
          style={{
            color: mode === 'config' ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: mode === 'config' ? '2px solid var(--accent)' : '2px solid transparent',
          }}
        >
          Configure
        </button>
        <button
          type="button"
          data-testid="planner-mode-review"
          onClick={() => setMode('review')}
          disabled={!draft.data || draft.data.plan === null}
          className="flex-1 py-2 text-sm disabled:opacity-50"
          style={{
            color: mode === 'review' ? 'var(--accent)' : 'var(--text-muted)',
            borderBottom: mode === 'review' ? '2px solid var(--accent)' : '2px solid transparent',
          }}
        >
          Review
        </button>
      </div>

      {mode === 'config' ? (
        <PlannerConfig
          onGenerated={() => {
            void draft.refresh();
            setMode('review');
          }}
        />
      ) : draft.data && draft.data.plan !== null && draft.data.days.length > 0 ? (
        <PlanDraftReview
          draft={draft.data}
          onSwap={async (id, recipeId) => {
            await draft.swap(id, { recipe_id: recipeId });
          }}
          onDelete={async (id) => {
            await draft.remove(id);
          }}
          onConfirm={async () => {
            const res = await draft.confirm();
            onNavigate?.('calendar');
            return res;
          }}
        />
      ) : (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center"
          data-testid="planner-empty"
        >
          <p style={{ color: 'var(--text-muted)' }}>No draft plan yet.</p>
          <button
            type="button"
            data-testid="planner-empty-cta"
            onClick={() => setMode('config')}
            className="flex min-h-[44px] items-center justify-center rounded-md border px-4 text-sm font-medium"
            style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
          >
            Generate one
          </button>
        </div>
      )}

      {draft.error ? (
        <div
          className="m-4 rounded border p-2 text-sm"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
          data-testid="planner-error"
          role="alert"
        >
          {draft.error}
        </div>
      ) : null}
    </section>
  );
}